<template>
  <aside
    ref="target"
    @mouseover="hoverLeftBar(true)"
    @mouseleave="hoverLeftBar(false)"
    v-bind="{'aria-labelledby': 'primary-heading'}"
    class="transition-all duration-300 fixed z-20 w-62.5 bg-white flex-shrink-0 overflow-hidden h-full items-center shadow-card"
    :class="{ ' hidden-aside w-17 ': isTitleHidden }"
  >
    <div class="flex flex-col mx-auto items-stretch h-full">
      <small class="absolute pl-2 left-0 bottom-0 italic text-cyan-800">{{ version }}</small>

      <!-- Header -->
      <div class="h-19.5 flex items-center relative">
        <router-link
          v-if="(isSBOpen && !isSBPin) || isSBPin"
          :to="{ name: 'Devices' }"
          class="transition-opacity duration-300 opacity-1 p-5 block"
          :class="{ 'opacity-0': isTitleHidden }"
        >
          <img src="@/assets/images/tss_logo_v3.png" alt="..." class="max-h-8 max-w-full align-middle" />
        </router-link>
        
        <div class="ml-auto">
          <div class="lg:col-span-10 xl:col-span-10 flex">
            <div class="hidden lg:block flex-grow">
              <div class="flex items-center space-x-2 2xl:space-x-4 text-black px-6">
                <MenuIcon v-if="!isSBPin" class="cursor-pointer h-6 w-5" @click="handleMenuClick" />
                <MenuAlt1Icon v-else class="cursor-pointer h-6 w-5" @click="handleMenuClick" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu -->
      <div class="flex-1 overflow-y-hidden">
        <el-scrollbar>
          <el-menu
            ref="target"
            class="text-dark-20 before:block before:md:mt-4 mt-0"
            v-if="openSubMenu.length"
            :default-openeds="openSubMenu"
          >
            <template v-for="(item, index) in menuItems" :key="index">
              <el-sub-menu
                v-if="item.children"
                :index="`${index}`"
                class="relative rounded-lg mx-2"
                :class="{
                  ' bg-slate-100/50 ': isRouteActive(item.name),
                  ' arrow-left ': isTitleHidden,
                }"
              >
                <template #title>
                  <span
                    v-if="isRouteActive(item.name)"
                    class="absolute inset-y-1 -left-2 w-0.5 h-5/6 rounded-tr-lg rounded-br-lg bg-indigo-410"
                    v-bind="{'aria-hidden': 'true'}"
                  />
                  <span
                    class="inline-flex pl-1.5 items-center w-full text-0.95 my-0.5 font-normal transition-colors duration-150 hover:text-gray-500/100 focus:text-gray-800"
                  >
                    <div>
                      <em class="h-5 w-6 block">
                        <component
                          :is="item.icon"
                          :class="` w-5 mx-auto ${item.color}`"
                          v-bind="{'aria-hidden': 'true'}"
                        />
                      </em>
                    </div>
                    <span
                      class="transition-opacity duration-300 opacity-1 ml-3 text-0.95 font-normal"
                      :class="{ 'opacity-0': isTitleHidden }"
                      >{{ item.title }}
                    </span>
                  </span>
                </template>

                <el-menu-item-group v-if="item.hasChild" class="flex flex-col">
                  <el-menu-item
                    v-for="(subItem, subIndex) in item.children"
                    :key="subIndex"
                    :index="`${index}-${subIndex}`"
                    class="relative flex flex-row h-11.25 rounded-lg mb-px mt-0.5"
                    :class="{
                      ' bg-slate-100/50': isRouteActive(subItem.name),
                    }"
                  >
                    <span
                      v-if="isRouteActive(subItem.name)"
                      class="absolute inset-y-1 -left-2 w-0.5 h-5/6 rounded-tr-lg rounded-br-lg bg-indigo-410"
                      v-bind="{'aria-hidden': 'true'}"
                    />
                    <router-link
                      :title="subItem.title"
                      :to="{ name: subItem.name }"
                      class="inline-flex ml-2 items-center w-full h-full text-0.95 my-0.5 font-normal transition-all duration-200 hover:text-gray-500/100 focus:text-gray-800"
                      :class="{
                        ' text-gray-800 ': isRouteActive(subItem.name),
                        ' ml-4.5 ': isSBOpen || isSBPin,
                      }"
                    >
                      <div>
                        <em class="h-5 w-6 block">
                          <component
                            :is="subItem.icon"
                            :class="` w-5 mx-auto ${subItem.color}`"
                            v-bind="{'aria-hidden': 'true'}"
                          />
                        </em>
                      </div>
                      <span
                        class="transition-opacity duration-300 opacity-1 ml-3 text-0.95 font-normal"
                        :class="{ 'opacity-0': isTitleHidden }"
                        >{{ subItem.title }}
                      </span>
                    </router-link>
                  </el-menu-item>
                </el-menu-item-group>
              </el-sub-menu>

              <el-menu-item
                v-else
                :index="`${index}`"
                class="relative rounded-lg mx-2"
                :class="{
                  ' bg-slate-100/50 ': isRouteActive(item.name),
                }"
              >
                <template #title v-if="item.separator===false">
                  <span
                    v-if="isRouteActive(item.name)"
                    class="absolute inset-y-1 -left-2 w-0.5 h-5/6 rounded-tr-lg rounded-br-lg bg-indigo-410"
                    v-bind="{'aria-hidden': 'true'}"
                  />
                  <router-link
                    :title="item.title"
                    :to="{ name: item.name }"
                    class="inline-flex pl-1.5 items-center w-full h-full text-0.95 my-0.5 font-normal transition-colors duration-150 hover:text-gray-500/100 focus:text-gray-800"
                    :class="{ ' text-gray-800 ': isRouteActive(item.name) }"
                  >
                    <div>
                      <em class="h-5 w-6 block">
                        <component
                          :is="item.icon"
                          :class="` w-5 mx-auto ${item.color}`"
                          v-bind="{'aria-hidden': 'true'}"
                        />
                      </em>
                    </div>
                    <span
                      class="transition-opacity duration-300 opacity-1 ml-3 text-0.95 font-normal"
                      :class="{ 'opacity-0': isTitleHidden }"
                      >{{ item.title }}
                    </span>
                  </router-link>
                </template>

                <template #title v-if="item.separator===true" >
                  <span
                    v-if="isRouteActive(item.name)"
                    class="absolute inset-y-1 -left-2 w-0.5 h-5/6 rounded-tr-lg rounded-br-lg bg-indigo-410"
                    v-bind="{'aria-hidden': 'true'}"
                  />
                  <span
                    class="transition-opacity duration-300 opacity-1 ml-3 text-0.95 font-normal"
                    :class="{ 'opacity-0': isTitleHidden }"
                    >{{ item.title }}
                  </span>
                </template>
              </el-menu-item>
            </template>
          </el-menu>
        </el-scrollbar>
      </div>
    </div>
  </aside>
</template>

<script lang="ts">

import { defineComponent, ref, computed, onMounted, onUnmounted, onBeforeMount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import navigation from './SidebarNav'
import { BellIcon, MenuIcon, MenuAlt1Icon, ColorSwatchIcon } from '@heroicons/vue/outline'
import useStore from 'store'
import { onClickOutside } from '@vueuse/core'
import env from 'core/env'
import { checkIsMobile } from 'utils/index'
import { Promotion } from '@element-plus/icons-vue'
import router from '../../router';

interface MenuItem {
  name: string;
  children?: MenuItem[];
  icon?: string;
  color?: string;
  title: string;
  separator?: boolean;
  hasChild?: boolean;
  meta?: {
    title: string;
    icon?: string;
    color?: string;
  };
}

export default defineComponent({
  name: 'Sidebar',
  components: {
    BellIcon,
    MenuIcon,
    MenuAlt1Icon,
    ColorSwatchIcon,
    Promotion,
  },
  setup() {
    const { t } = useI18n()
    const route = useRoute()
    const store = useStore()

    const menuItems = ref<MenuItem[]>([])
    const isPagesMenuOpen = ref(false)
    const isSideMenuOpen = ref(false)
    const target = ref(null)
    const version = ref(env('VITE_APP_VERSION'))
    const documentHref = ref(env('VITE_DOCUMENT_ENDPOINT'))
    const isMobile = checkIsMobile()

    const openSubMenu = ref<string[]>([]);

    const getOpenMenuIndex = () => {
      const currentRouteName = route.name;
      for (let i = 0; i < menuItems.value.length; i++) {
        const item = menuItems.value[i];
        if (item.children) {
          for (let j = 0; j < item.children.length; j++) {
            if (item.children[j].name === currentRouteName) {
              return [`${i}`];
            }
          }
        }
      }

      return ['DashBoard'];
    };
    
    const handleOnResize = () => {
      if (window.innerWidth < 1024) {
        store.dashboard.setIsSBOpen(false)
        store.dashboard.setIsSBPin(false)
      }
    }

    const updateMenu = (section: string) => {
      menuItems.value = [...navigation(section, t)]
      openSubMenu.value = getOpenMenuIndex()
    }

    onClickOutside(target, (_) => {
      if (window.innerWidth < 1024) store.dashboard.setIsSBOpen(false)
    })

    onBeforeMount(() => {
      if (isMobile || window.innerWidth < 1024) {
        store.dashboard.setIsSBOpen(false)
        store.dashboard.setIsSBPin(false)
      }
    })

    const getSectionFromRoute = (path: string) => {
      if (path.includes('/administration')) return 'administration'
      if (path.includes('/back-office')) return 'back-office'
      if (path.includes('/dealer')) return 'dealer'
      return 'dashboard'
    }

    onMounted(() => {
      const section = getSectionFromRoute(router.currentRoute.value.fullPath)
      updateMenu(section)

      window.addEventListener('resize', handleOnResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleOnResize)
    })

    router.afterEach((to) => {
      openSubMenu.value = []
      const section = getSectionFromRoute(to.fullPath)
      updateMenu(section)
    })

    const isSBPin = computed<boolean>(() => store.dashboard.isSBPin)
    const isSBOpen = computed<boolean>(() => store.dashboard.isSBOpen)
    const isTitleHidden = computed(() => !isSBOpen.value && !isSBPin.value)
    const isRouteActive = (name: string) => route.name === name

    const hoverLeftBar = (v: boolean) => {
      if (!isMobile && window.innerWidth > 1023) {
        store.dashboard.setIsSBOpen(v)
      }
    }

    const handleMenuClick = () => {
      store.dashboard.toggleMenu()
    }

    return {
      isPagesMenuOpen,
      isSideMenuOpen,
      menuItems,
      route,
      isSBPin,
      isSBOpen,
      isTitleHidden,
      target,
      version,
      documentHref,
      hoverLeftBar,
      handleMenuClick,
      isRouteActive,
      getOpenMenuIndex,
      openSubMenu,
    }
  },
})

</script>

<style scoped>

  .hidden-aside {
    @apply -translate-x-3/2 lg:translate-x-0 lg:block;
  }

  .el-menu-item {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

</style>
